// `/sprints` — the weekly sprint check-in (Deborah's Q4 plan, 1 Nov). Every role, like All accounts.
//
// A sprint picker (newest first, kept in `?sprint=`), the sprint's totals in one strip — loaded,
// worked, replied, meetings, pipeline — and its accounts with owner, Fit and Engagement, first and
// last touch, replies, meetings and opportunities. A sprint is a checked-in pool with sprint
// metadata (`agent_api/pools.py`), joined on serve into `row.pools`; the counting rules are the
// board's own (`book/sprintTotals.ts`), so this page and My Accounts agree on every account.
// A row opens the same account panel as the board.

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AccessDeniedError, fetchBook } from '../api/client';
import type { BookPayload, EnrichCandidate } from '../api/types';
import { useAuthContext } from '../auth/AuthContext';
import { DeniedGate } from '../auth/Gate';
import { PageChrome } from '../components/PageChrome';
import { SectionHead } from '../components/SectionHead';
import { DenseTable } from '../book/DenseTable';
import { attachRanking } from '../book/derived';
import { attachScores } from '../book/scores';
import { cycleSort, sortRows } from '../book/sort';
import type { SortState } from '../book/sort';
import { sprintColumns } from '../book/sprintColumns';
import { pickedSprintKey, rowsInSprint, shareOf, sprintOptions, sprintTotals } from '../book/sprintTotals';
import type { SprintOption, SprintTotals } from '../book/sprintTotals';
import { sprintWeekText } from '../book/sprints';
import { useScores } from '../book/useScores';
import { formatCount, formatMoney } from '../lib/format';
import { offBoardPanel } from '../lib/roleViews.ts';
import { applyEnrichedPerson } from '../lib/enrichedBook';
import { Sidebar } from '../sidebar/Sidebar';

const PARAM_SPRINT = 'sprint';
// The most recently worked accounts first; untouched ones sink (sort.ts puts nulls last).
const DEFAULT_SORT: SortState = { key: 'last_touch', direction: -1 };
const GROUP_ALL = 0;

// "2–8 Nov · Financial services · Eva": what the picker cannot say in its label.
function sprintFacts(option: SprintOption): string {
  return [sprintWeekText(option), option.vertical, option.owner].filter(Boolean).join(' · ');
}

function TotalsStrip({ totals }: { totals: SprintTotals }) {
  const tiles = [
    { label: 'Loaded', value: formatCount(totals.loaded), foot: 'Sprint accounts on the book' },
    { label: 'Worked', value: formatCount(totals.worked), foot: `${shareOf(totals.worked, totals.loaded)} touched at least once` },
    { label: 'Replied', value: formatCount(totals.replied), foot: `${shareOf(totals.replied, totals.loaded)} replied to anyone here` },
    { label: 'Meetings', value: formatCount(totals.meetings), foot: 'Accounts with a meeting held or booked' },
    {
      label: 'Pipeline', value: formatMoney(totals.pipeline) ?? '$0',
      foot: `${formatCount(totals.with_opportunity)} with an open opportunity of ours`,
    },
  ];
  return (
    <div className="strip sprinttotals" aria-label="Sprint totals">
      {tiles.map(tile => (
        <div className="tile" key={tile.label}>
          <div className="lab">{tile.label}</div>
          <div className="val">{tile.value}</div>
          <div className="foot">{tile.foot}</div>
        </div>
      ))}
    </div>
  );
}

export function SprintsView() {
  const { getToken, email, signOut } = useAuthContext();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openAccountId, setOpenAccountId] = useState<string | null>(searchParams.get('account'));
  const [chosenSort, setChosenSort] = useState<SortState | null>(null);

  const bookQuery = useQuery({ queryKey: ['book'], queryFn: () => fetchBook(getToken) });
  const book = bookQuery.data;
  const viewer = book?.viewer;
  const scores = useScores(getToken, viewer);

  const refresh = useMutation({
    mutationFn: () => fetchBook(getToken, true),
    onSuccess: payload => queryClient.setQueryData<BookPayload>(['book'], payload),
  });

  // The sprints on the book, and the one picked (the link's, else the newest).
  const scoredAccounts = useMemo(() => attachScores(book?.accounts ?? [], scores.payload), [book?.accounts, scores.payload]);
  const options = useMemo(() => sprintOptions(scoredAccounts), [scoredAccounts]);
  const sprintKey = pickedSprintKey(options, searchParams.get(PARAM_SPRINT));
  const picked = options.find(option => option.key === sprintKey) ?? null;

  // The sprint's rows, its totals, and the table in the chosen order.
  const sprintRows = useMemo(() => rowsInSprint(scoredAccounts, sprintKey), [scoredAccounts, sprintKey]);
  const totals = useMemo(() => sprintTotals(sprintRows), [sprintRows]);
  const columns = useMemo(() => sprintColumns(scores.shown), [scores.shown]);
  const sort = chosenSort ?? DEFAULT_SORT;
  const tableRows = useMemo(() => {
    if (!book) return [];
    const ranked = attachRanking(sprintRows, null, book.generated_at, book.today)
      .map(row => ({ ...row, board_pref: null, group: GROUP_ALL }));
    return sortRows(ranked, sort, columns);
  }, [book, sprintRows, sort, columns]);

  const today = useMemo(() => new Date((book?.today ?? '1970-01-01') + 'T00:00:00Z'), [book?.today]);
  const cellContext = useMemo(
    () => ({ today, staleDays: book?.stale_days ?? 14, fitCeiling: book?.fit_score_ceiling }),
    [today, book?.stale_days, book?.fit_score_ceiling],
  );

  function pickSprint(next: string) {
    setChosenSort(null);
    setSearchParams(current => {
      const params = new URLSearchParams(current);
      params.set(PARAM_SPRINT, next);
      return params;
    }, { replace: true });
  }

  function applyEnrichedContact(contactId: string, candidate: EnrichCandidate) {
    queryClient.setQueryData<BookPayload>(['book'], current => current && applyEnrichedPerson(current, contactId, candidate));
  }

  if (bookQuery.error instanceof AccessDeniedError) {
    return <DeniedGate message={bookQuery.error.message} email={email} onSignOut={signOut} />;
  }

  const openAccount = openAccountId ? (book?.accounts.find(row => row.id === openAccountId) ?? null) : null;

  const body = () => {
    if (bookQuery.isPending || scores.waiting) return <div className="loading">Loading sprints…</div>;
    if (bookQuery.error) {
      return (
        <div className="err">
          <b>Could not load the book.</b>
          <br />
          {(bookQuery.error as Error).message}
        </div>
      );
    }
    if (!book) return null;
    if (!picked) {
      return (
        <>
          <SectionHead title="Sprints" />
          <p className="briefempty">No account on the book is in a sprint yet.</p>
        </>
      );
    }

    return (
      <>
        <SectionHead title="Sprints" count={`${formatCount(options.length)} on the book`} />
        <div className="sprintbar">
          <select className="aaselect sprintpicker" aria-label="Sprint" value={sprintKey}
            onChange={event => pickSprint(event.target.value)}>
            {options.map(option => (
              <option key={option.key} value={option.key}>{option.label} ({formatCount(option.count)})</option>
            ))}
          </select>
          <span className="sprintfacts">{sprintFacts(picked)}</span>
          <a className="sprintlink" href={`/all-accounts?pool=${encodeURIComponent(picked.key)}`}>Open in All accounts</a>
        </div>
        <TotalsStrip totals={totals} />
        <p className="sprintnote">
          All-time, as on the board: a reply or meeting from before the sprint started counts.
        </p>
        <div className="boardv2 aatablearea sprinttablearea">
          <DenseTable
            rows={tableRows}
            columns={columns}
            context={cellContext}
            sort={sort}
            onSort={key => setChosenSort(cycleSort(sort, key, DEFAULT_SORT))}
            openAccountId={openAccountId}
            onToggleAccount={accountId => setOpenAccountId(current => (current === accountId ? null : accountId))}
            onSelectAccount={accountId => setOpenAccountId(accountId)}
            canEditPrefs={false}
            pendingPrefId={null}
            onSetPref={() => {}}
            groupLabels={{ [GROUP_ALL]: picked.label }}
            showRowMenu={false}
            tableClassName="sprinttable"
          />
        </div>
      </>
    );
  };

  return (
    <>
      <PageChrome
        freshness={book?.freshness ?? null}
        build={book?.build}
        viewer={book?.viewer}
        isRebuilding={refresh.isPending}
        onRebuild={() => refresh.mutate()}
      >
        {body()}
      </PageChrome>
      <Sidebar
        account={openAccount}
        book={book ?? null}
        onClose={() => setOpenAccountId(null)}
        onContactEnriched={applyEnrichedContact}
        onBookChanged={() => { void queryClient.invalidateQueries({ queryKey: ['book'] }); }}
        page={offBoardPanel(viewer?.role)}
      />
    </>
  );
}
